import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useRailScroll } from "@/lib/useRailScroll";
import { useExplore } from "@/lib/ExploreContext";
import { useLang } from "@/lib/i18n";
import { listingPath } from "@/lib/businessSlug";
import { bucketFor } from "@/data/experiences";
import { isRideService, viatorTitleLabel } from "@/lib/viator";
import BookingPartnerLogo from "@/components/yts/BookingPartnerLogo";
import { base44 } from "@/api/base44Client";

const THEMES = [
  "Culture, Music & Workshops", "Food, Rum & Coffee", "Horseback Riding",
  "El Yunque & Rainforest", "Kayak & Paddleboarding",
  "Sailing, Catamaran & Sunset Cruises", "Snorkeling & Sea Life",
  "Adventure & Off-Road", "Beach & Island Day Trips",
  "Old San Juan & City Tours", "Fishing Charters",
];

const imageOf = (item) => item?.cover_image || item?.storefront_image ||
  item?.image || item?.image_url || item?.gallery_images?.find(Boolean) ||
  item?.images?.find(Boolean) || "";
const isVisible = (item) => item?.id && item.status !== "Archived" &&
  item.status !== "Draft" && !item.coming_soon;
const isLoopMain = (item) => /^loop experience (?:emoped|e-moped) rentals/i.test(String(item.name || ""));
const isExcludedSupplier = (item) => (
  item?.affiliate_provider === "FareHarbor" &&
  /\/items\/(?:484249|551117)(?:\D|$)/.test(String(item.booking_url || item.primary_action_url || ""))
);
const makeListing = (item) => ({
  provider: item.affiliate_provider === "FareHarbor" ? "fareharbor" : "local",
  key: `listing-${item.id}`, item,
  to: isLoopMain(item) ? "/loopexperience" : listingPath(item),
  image: imageOf(item),
  title: item.name, price: item.price_label || "",
  theme: bucketFor(item)
});

/**
 * Ten editorial experience picks. This is a curated collection, not a rating,
 * sales ranking, or claim that Puerto Rico itself is for sale.
 * LOOP is the featured local partner at position one, by editorial selection.
 */
export default function HomeBookingEdit() {
  const { ref: rail, nudge, canPrev, canNext } = useRailScroll();
  const { allListings = [], viatorListings = [], badBunnyListings = [], setActiveChip, clearFilters } = useExplore();
  const { lang } = useLang();
  const es = lang === "es";

  const cards = useMemo(() => {
    const excluded = new Set(badBunnyListings.map((item) => item.id));
    const loop = allListings.find((item) => isVisible(item) && isLoopMain(item) && imageOf(item));
    const local = allListings
      .filter((item) => isVisible(item) && item.category === "Experiences" &&
        !isLoopMain(item) && !isExcludedSupplier(item) && imageOf(item))
      .map(makeListing);
    const viator = viatorListings
      .filter((item) => item?.id && !excluded.has(item.id) && !isRideService(item) && item.booking_url && imageOf(item))
      .map((item) => ({
        provider:"viator", item, key:`viator-${item.id}`, image:imageOf(item),
        to:`/experience/${item.id}`, title:viatorTitleLabel(item,lang),
        price:item.price_label || "", theme:bucketFor(item)
      }));

    const chosen = [];
    const usedKeys = new Set();
    const usedThemes = new Set();
    const add = (card, uniqueTheme = false) => {
      if (!card || usedKeys.has(card.key) || (uniqueTheme && usedThemes.has(card.theme))) return false;
      chosen.push(card); usedKeys.add(card.key); usedThemes.add(card.theme); return true;
    };

    if (loop) add({ ...makeListing(loop), provider:"local", title:"LOOP Experience — eMoped Rentals & Tours" });
    // Give the island's music and cultural experiences a prominent early slot.
    const all = [...local, ...viator];
    add(all.find((card) => /bomba|salsa|plena|dance class/i.test(card.title)));
    // Wellness is an editorial pick, not a paid ranking. LOOP remains first.
    add(all.find((card) => card.item.product_code === "5642477P1"));
    THEMES.forEach((theme) => {
      if (chosen.length < 10) add(all.find((card) => card.theme === theme && !usedKeys.has(card.key)), true);
    });
    all.forEach((card) => { if (chosen.length < 10) add(card); });
    return chosen.slice(0, 10);
  }, [allListings, viatorListings, badBunnyListings, lang]);

  const track = (card, position) => {
    try {
      Promise.resolve(base44.analytics.track({
        eventName: "favorite_experience_click",
        properties: { experience_id: card.item.id, provider: card.provider, position, placement:"home-favorites" }
      })).catch(() => {});
    } catch { /* Never block navigation. */ }
  };
  const browseAll = () => {
    // This action promises *all* experiences, so clear any prior search,
    // subcategory or neighborhood constraint before opening the collection.
    clearFilters?.();
    setActiveChip?.("Experiences");
    window.scrollTo({ top:0, behavior:"smooth" });
  };

  return (
    <section id="home-bookable" data-tour="bookable" aria-label={es ? "Favoritos de Ya Tú Sabes" : "Ya Tú Sabes Favorites"}
      className="scroll-mt-[130px] pt-7 sm:pt-10">
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-3 border-t-2 border-[#171717] pt-3 dark:border-white">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[.11em] text-[#b43a35] dark:text-[#ffa7a1]">
              {es ? "LA SELECCIÓN LOCAL / PUERTO RICO" : "THE LOCAL EDIT / PUERTO RICO"}
            </p>
            <h2 className="mt-1.5 text-[clamp(28px,5vw,49px)] font-extrabold leading-[1.02] tracking-[-.045em] text-[#171717] dark:text-white">
              {es ? "Favoritos de Ya Tú Sabes." : "Ya Tú Sabes Favorites."}
            </h2>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <button type="button" onClick={() => nudge(-1)} disabled={!canPrev}
              aria-label={es ? "Favoritos anteriores" : "Previous favorites"}
              className="grid h-9 w-9 place-items-center rounded-md border border-black/20 text-[#171717] disabled:opacity-30 dark:border-white/30 dark:text-white">
              <ChevronLeft size={18} aria-hidden="true"/>
            </button>
            <button type="button" onClick={() => nudge(1)} disabled={!canNext}
              aria-label={es ? "Más favoritos" : "Next favorites"}
              className="grid h-9 w-9 place-items-center rounded-md border border-black/20 text-[#171717] disabled:opacity-30 dark:border-white/30 dark:text-white">
              <ChevronRight size={18} aria-hidden="true"/>
            </button>
          </div>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[12px] leading-[1.5] text-[#696660] dark:text-white/65">
            {es
              ? "10 experiencias para conocer Puerto Rico a tu manera."
              : "10 experiences worth exploring, the local way."}
          </p>
          <button type="button" onClick={browseAll}
            className="inline-flex min-h-8 items-center gap-1 text-[11px] font-bold underline underline-offset-4 hover:opacity-70 dark:text-white">
            {es ? "Todas las experiencias" : "All experiences"} <ArrowUpRight size={15} aria-hidden="true"/>
          </button>
        </div>
      </div>

      <div ref={rail} tabIndex={0} role="region" aria-roledescription="carousel"
        aria-label={es ? "Carrusel de diez favoritos" : "Ten curated favorites"}
        onKeyDown={(event) => {
          if (event.target === event.currentTarget && ["ArrowLeft","ArrowRight"].includes(event.key)) {
            event.preventDefault(); nudge(event.key === "ArrowRight" ? 1 : -1);
          }
        }}
        className="no-scrollbar mx-auto mt-4 flex max-w-[1280px] snap-x snap-mandatory gap-3.5 overflow-x-auto px-4 pb-3 scroll-smooth [scrollbar-width:none] sm:gap-4 sm:px-6 lg:px-8">
        {cards.length ? cards.map((card, i) => (
          <Link key={card.key} to={card.to} onClick={() => track(card, i + 1)}
            className={`group flex shrink-0 snap-start flex-col overflow-hidden rounded-[2px] border border-[#e4e0d9] bg-white text-[#171717] transition-colors hover:border-[#9c948a] dark:border-white/20 dark:bg-[#272729] dark:text-white ${i === 0 ? "w-[min(79vw,344px)] sm:w-[340px]" : "w-[min(70vw,278px)] sm:w-[272px]"}`}>
            <div className="relative aspect-[5/4] w-full overflow-hidden bg-[#222]">
              <img src={card.image} alt={card.title} loading={i < 2 ? "eager" : "lazy"} decoding="async"
                className="h-full w-full object-cover transition-transform duration-700 motion-safe:group-hover:scale-[1.035]"/>
              {card.provider !== "local" && (
                <span className="absolute left-2 top-2"><BookingPartnerLogo provider={card.provider}/></span>
              )}
              {i === 0 && (
                <span className="absolute left-2 top-2 bg-[#171717] px-2 py-1.5 text-[9px] font-extrabold uppercase tracking-[.07em] text-white">
                  {es ? "SOCIO LOCAL" : "LOCAL PARTNER"}
                </span>
              )}
              <span className="absolute bottom-2 left-2 bg-white/95 px-2 py-1 text-[10px] font-extrabold tabular-nums text-[#171717]">
                {String(i + 1).padStart(2, "0")} / {String(cards.length).padStart(2, "0")}
              </span>
            </div>
            <div className="flex flex-1 flex-col px-4 pb-3.5 pt-3.5">
              <p className="text-[9px] font-bold uppercase tracking-[.08em] text-[#ac3935] dark:text-[#ffb8aa]">
                {i === 0 ? (es ? "EN PORTADA" : "FEATURED FAVORITE")
                  : (es ? "EXPERIENCIA LOCAL" : "EXPERIENCE PICK")}
              </p>
              <h3 className="mt-1.5 line-clamp-2 min-h-[2.3em] text-[clamp(17px,2vw,21px)] font-extrabold leading-[1.14] tracking-[-.034em]">{card.title}</h3>
              <div className="mt-auto flex min-h-9 items-center justify-between gap-2 border-t border-black/10 pt-2 text-[11px] font-bold dark:border-white/15">
                <span>{es ? "Ver experiencia" : "View experience"}</span>
                <ArrowUpRight size={16} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"/>
              </div>
            </div>
          </Link>
        )) : (
          <p role="status" className="py-6 text-[12px] text-[#777]">
            {es ? "Explora las experiencias disponibles en la guía." : "Explore experiences across the guide."}
          </p>
        )}
      </div>
      <p className="mx-auto max-w-[1240px] px-4 text-[10px] leading-relaxed text-[#77736e] dark:text-white/60 sm:px-6 lg:px-8">
        {es ? "Selección editorial de experiencias. Cada proveedor gestiona sus propios detalles y reservaciones, cuando estén disponibles."
            : "An editorial selection. Experience details and any reservations are managed by individual providers."}
      </p>
    </section>
  );
}
